import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../components";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import DB_Status from "../../model/enum/DB_Status";
import Message from "../../model/message";
import Client from "../../model/client";
import Status from "../../model/status";
import EmailService from "../../service/EmailService";
import AlertService from "../../service/AlertService";
import SmsService from "../../service/SmsService";
import ToastMessages from "../../model/enum/ToastMessages";
import DeleteModal from "../modal/DeleteModal";
import EditIcon from "../../assets/admin/edit-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import useStyles from "./styles";
import ApiService from "../../service/ApiService";
import PageResultResponse from "../../model/pageResultResponse";
import Misc from "../../utils/Misc";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const ContactModal = ({ contact, resetList }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = contact != null;
  const id = edit ? contact.id : 0;
  const [client, setClient] = useState(edit ? contact.Client : null);
  const [clients, setClients] = useState<Client[]>([]);
  const [text, setText] = useState(edit ? contact.text : null);
  const [status, setStatus] = useState(edit ? contact.Status : null);
  const [statusList, setStatusList] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);
  const [notify, setNotify] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientsData, statusData] = await Promise.all([
          ApiService.get<PageResultResponse>(DatabaseEntities.CLIENTS),
          ApiService.get<Status[]>(`${DatabaseEntities.STATUS}?type=General`),
        ]);
        setClients(clientsData.items);
        setStatusList(statusData);
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };
    if (open && Misc.isArrayEmpty(clients) && Misc.isArrayEmpty(statusList)) {
      fetchData();
    }
  }, [open, clients, statusList]);

  const handleClient = (e: any) => {
    let value = e.target.value;
    var clt = clients.find((x) => x.id == value);
    setClient(clt);
  };

  const handleMessage = (e: any) => setText(e.target.value);
  const handleStatus = (e: any) => {
    let value = e.target.value;
    var stt = statusList.find((x) => x.id == value);
    setStatus(stt);
  };

  const handleNotification = () => setNotify((notify) => !notify);

  const handleUpdate = async (payload: Message) => {
    await ApiService.put<Message>(
      `${DatabaseEntities.MESSAGES}/${payload.id}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Message) => {
    await ApiService.delete<any>(`${DatabaseEntities.MESSAGES}/${payload.id}`)
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = () => {
    return text === "" || !client || !status;
  };

  const register = async () => {
    if (invalidInput()) return;

    const payload: Message = {
      id: id,
      text: text,
      clientId: client.id,
      statusId: status.id,
    };

    if (edit) {
      await handleUpdate(payload)
        .then(async () => {
          if (notify) {
            if (client && client.firstName && client.email && client.phone) {
              if (status.name === DB_Status.ACCEPTED) {
                await SmsService.send(client.phone, "Contact Accepted");
                await EmailService.sendEmail(
                  client.firstName,
                  client.email,
                  "Contact Accepted"
                );
              } else if (status.name === DB_Status.CANCELED) {
                await SmsService.send(client.phone, "Contact Denied");
                await EmailService.sendEmail(
                  client.firstName,
                  client.email,
                  "Contact Denied"
                );
              }
            }
          }
        })
        .catch(() => {
          AlertService.error(ToastMessages.UPDATE_FAILED);
        });
    }
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add Contact
        </Btn>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              {edit ? (
                <DeleteModal item={contact} handleDelete={handleDelete} />
              ) : (
                <Typography className={classes.title}>
                  Add contact message
                </Typography>
              )}
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            <hr />
            <div className={classes.form}>
              <select
                className={classes.input}
                onChange={handleClient}
                value={client?.id}
              >
                <option value="">Select Client</option>
                {clients.map((usr: Client) => {
                  return (
                    <option key={usr.id} value={usr.id}>
                      {usr.firstName} {usr.lastName}
                    </option>
                  );
                })}
              </select>
              <select
                className={classes.input}
                onChange={handleStatus}
                value={status?.id}
              >
                <option value="">Status</option>
                {statusList.map((stts) => {
                  return (
                    <option key={stts.id} value={stts.id}>
                      {stts.name}
                    </option>
                  );
                })}
              </select>
              <textarea
                className={classes.textarea}
                value={text}
                onChange={handleMessage}
                placeholder="Message"
              />
            </div>
            <div className={classes.inputGroup}>
              <input
                type="checkbox"
                className={classes.checkbox}
                onChange={handleNotification}
                checked={notify}
              />
              <Typography className={classes.checkboxtext}>
                Send notification{" "}
              </Typography>
            </div>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.button}
                onClick={register}
                disabled={invalidInput()}
              >
                {edit ? "Save" : "Create"}
              </Btn>
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default ContactModal;
